import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { COMMISSION_PRESETS } from '@/components/filters/date-range-presets';
import { TabContent, TopNavWithTabs } from '@/components/scaffold/top-nav';
import { PageHeader } from '@/components/ui';
import { usePageTitleStore } from '@/stores/page-title';
import { VStack } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CommissionHistoryView } from './_components/CommissionHistoryView';
import CommissionStatsGrid from './_components/CommissionStatsGrid';
import { InvoicesTabContent } from './_components/InvoicesTabContent';
import { PayoutsTabContent } from './_components/PayoutsTabContent';
import { PendingCommissionsTable } from './_components/PendingCommissionsTable';
import UnfinishedPayoutsNotice from './_components/UnfinishedPayoutsNotice';

interface CommissionsViewProps {}

export const CommissionsPage: React.FC<CommissionsViewProps> = () => {
  const { t } = useTranslation();
  const setTitle = usePageTitleStore((state) => state.setTitle);

  useEffect(() => {
    setTitle(t('commissions.page.title'));
    return () => setTitle(null);
  }, [setTitle, t]);

  const tabs = [
    {
      label: t('commissions.tabs.commissions'),
      value: 'commissions',
    },
    {
      label: t('commissions.tabs.history'),
      value: 'history',
    },
    {
      label: t('commissions.tabs.payouts'),
      value: 'payouts',
    },
    {
      label: t('commissions.tabs.invoices'),
      value: 'invoices',
    },
  ];

  return (
    <TopNavWithTabs tabs={tabs} defaultTab="commissions">
      <TabContent value="commissions">
        <VStack gap={8} align="stretch">
          <PageHeader
            title={t('commissions.tabs.commissions')}
            description={t('commissions.page.commissionsDescription')}
            marginBottom={0}
            actions={
              <DateRangeFilter
                paramPrefix="stats"
                presets={COMMISSION_PRESETS}
                showPresets={true}
                defaultPreset="30d"
              />
            }
          />

          <CommissionStatsGrid />
          {/* The notice explains commissions missing from the table, so it sits with it. */}
          <VStack gap={4} align="stretch">
            <UnfinishedPayoutsNotice />
            <PendingCommissionsTable />
          </VStack>
        </VStack>
      </TabContent>

      <TabContent value="history">
        <VStack gap={8} align="stretch">
          <PageHeader
            title={t('commissions.tabs.history')}
            description={t('commissions.page.historyDescription')}
            marginBottom={0}
          />
          <CommissionHistoryView />
        </VStack>
      </TabContent>

      <TabContent value="payouts">
        <VStack gap={8} align="stretch">
          <PageHeader
            title={t('commissions.tabs.payouts')}
            description={t('commissions.page.payoutsDescription')}
            marginBottom={0}
          />
          <PayoutsTabContent />
        </VStack>
      </TabContent>

      <TabContent value="invoices">
        <VStack gap={8} align="stretch">
          <PageHeader
            title={t('commissions.tabs.invoices')}
            description={t('commissions.page.invoicesDescription')}
            marginBottom={0}
          />
          <InvoicesTabContent />
        </VStack>
      </TabContent>
    </TopNavWithTabs>
  );
};
